import { ChartData, ChartDataset, ChartType, DefaultDataPoint } from 'chart.js';
import { useMemo } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useAppSelector } from '../../../app/hooks';
import {
  BlueberryColorsMap,
  BlueberryFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  findHighestModelVersion,
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import { selectScanDataTableExists } from '../../duckDB/duckDBSlice';
import { DuckQueryReturnOneType } from '../../duckDB/hooks/useDuckQueryScanData';
import {
  selectCurrBlueberryClass,
  selectCurrFeature,
  selectCurrMeasure,
} from '../../select/selectSlice';
import { useUnits } from '../../settings/hooks/useUnits';
import {
  ScanDataEnum,
  convertMeasureCubeRanchToScanDataBlock,
} from '../scanDataDefinitions';
import { orderBlueberriesByRipenessOrder } from '../scanDataUtils';
import { useBlueberryFlowerFeatures } from './useBlueberryFlowerFeatures';
import { useBlueberryQueryValues } from './useBlueberryQueryValues';
import {
  OperatorEnum,
  ScanDataFilter,
  useScanDataQuery,
} from './useScanDataQuery';

interface ChartDataWithRawData<
  TType extends ChartType = ChartType,
  TData = DefaultDataPoint<TType>,
  TLabel = unknown
> extends ChartData<TType, TData, TLabel> {
  rawData: QueryReturnType[] | null;
}

interface QueryReturnType {
  [ScanDataEnum.rowside_id]: string;
  [ScanDataEnum.rowside_num]: number;
  [ScanDataEnum.view_direction]: number;
  [FeatureEnum.flower]: number;
  [FeatureEnum.flower_closed]: number;
  [FeatureEnum.flower_open]: number;
  [FeatureEnum.flower_petalfall]: number;
  [FeatureEnum.blueberry_Green]: number;
  [FeatureEnum.blueberry_Yellow]: number;
  [FeatureEnum.blueberry_Purple]: number;
  [FeatureEnum.blueberry_Blue]: number;
  total: number;
}

type ActiveFilters = {
  currBlueberryClass?: boolean;
};

const group = [
  ScanDataEnum.rowside_id,
  ScanDataEnum.rowside_num,
  ScanDataEnum.view_direction,
];

const order: [string, 'asc' | 'desc'][] = [[ScanDataEnum.rowside_num, 'asc']];

type Props = {
  activeFilters: ActiveFilters;
};

/**
 * Hook: useBlueberryEstimatedCountRowsideStackBar
 *
 * Purpose:
 * This hook is designed to fetch and format data for blueberry estimated counts per rowside.
 * It prepares data for rendering in a Chart.js bar chart and for CSV table outputs. The hook
 * dynamically includes or excludes flower data based on layout configuration and active filters.
 * Whether to show flowers in the stacked bar chart when available is handled in useBlueberryEstimatedCountRowsideStackBarChart.
 *
 * Functionality:
 * - Dynamically generates query filters and columns based on the latest model version, current feature, and measure settings.
 * - Conditionally includes flower features for chart visualization and CSV table output, as dictated by the layout configuration and active filters.
 * - Aggregates and formats data for Chart.js bar chart datasets, ensuring compatibility with both chart and CSV output requirements.
 * - Relies on external logic (handled in a different hook  useBlueberryEstimatedCountRowsideStackBarChart) to determine the rendering of flower data in the chart so that the CSV table still includes it if available.
 *
 * Parameters:
 * - activeFilters: Object containing states for filtering blueberry class and flower data in the output.
 *
 * Returns:
 * - An object comprising formatted chart data and raw query results, compatible with Chart.js bar charts and suitable for CSV table generation.
 *
 */
export const useBlueberryEstimatedCountRowsideStackBar = ({
  activeFilters,
}: Props): DuckQueryReturnOneType<ChartDataWithRawData<'bar'>> => {
  const units = useUnits();
  const currFeature = useAppSelector(selectCurrFeature);
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currScanDataMeasure =
    convertMeasureCubeRanchToScanDataBlock(currMeasure);
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);

  const flowerFeatures = useBlueberryFlowerFeatures();
  const queryValues = useBlueberryQueryValues();

  // For "blueberry", only model version and name are needed. Class color is arbitrary (e.g., blueberry_Blue). Model versions are synced.
  const latestService = findHighestModelVersion(
    servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
    [FeatureEnum.blueberry_Blue]
  );

  const filters = useDeepCompareMemo(() => {
    const newFilters = [];

    if (latestService) {
      newFilters.push(
        {
          col: ScanDataEnum.model_name,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelName],
        },
        {
          col: ScanDataEnum.model_version,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelVersion],
        }
      );

      if (activeFilters.currBlueberryClass) {
        newFilters.push({
          col: ScanDataEnum.class_name,
          op: queryValues.length === 1 ? OperatorEnum.eq : OperatorEnum.in,
          value:
            queryValues.length === 1
              ? queryValues.at(0)
              : orderBlueberriesByRipenessOrder({
                  arrayToOrder: queryValues as FeatureEnum[],
                }),
        } as ScanDataFilter);
      }
    }

    return newFilters;
  }, [latestService, activeFilters, currBlueberryClass, queryValues]);

  // Sum up all counts for each class (green, yellow, purple, blue, flowers - optional)
  const cols = useMemo(() => {
    if (!currScanDataMeasure) return [];

    const features = [...flowerFeatures, ...BlueberryFeatures];

    const featuresRipenessOrder = orderBlueberriesByRipenessOrder({
      arrayToOrder: features,
    });

    return [
      ScanDataEnum.rowside_id,
      ScanDataEnum.rowside_num,
      ScanDataEnum.view_direction,
      ...featuresRipenessOrder.map(
        (el) =>
          `ROUND(SUM(${ScanDataEnum.estimated_count}) FILTER (WHERE ${ScanDataEnum.class_name} = '${el}')) as ${el}`
      ),
      `ROUND(SUM(${ScanDataEnum.estimated_count}) FILTER (WHERE ${
        ScanDataEnum.class_name
      } in (${features.map((el) => `'${el}'`).join(', ')}))) as total`,
    ];
  }, [currScanDataMeasure, units, flowerFeatures]);

  const rowsideEstimatedCounts = useScanDataQuery<QueryReturnType>({
    cols,
    group,
    filters,
    order,
    skip:
      !latestService ||
      servicesAndMeasuresWithFeatureFilter.availableServicesRaw.length === 0 ||
      !(
        isBlueberryFeature(currFeature) || isBlueberryFlowerFeature(currFeature)
      ) ||
      !scanDataTableExists,
    disableClassFilter: true,
  });

  // Create datasets in chart.js bar chart format and sort by ripeness
  const chartData: ChartDataWithRawData<'bar'> = useMemo(() => {
    const datasets: ChartDataset<'bar', number[]>[] = [];
    const labels = [] as string[];

    const features = [...flowerFeatures, ...BlueberryFeatures];

    const featuresRipenessOrder = orderBlueberriesByRipenessOrder({
      arrayToOrder: features,
    });

    // generate datasets for chart.js bar chart
    if (rowsideEstimatedCounts.data) {
      const classRelatedResults = featuresRipenessOrder.map((bf) => ({
        label: bf,
        data: rowsideEstimatedCounts.data?.map(
          (el) => el[bf as keyof QueryReturnType] as number
        ),
      }));

      rowsideEstimatedCounts.data.forEach((el) =>
        labels.push(
          `${el[ScanDataEnum.rowside_num]} (${el[ScanDataEnum.view_direction]})`
        )
      );

      classRelatedResults.forEach((el) => {
        datasets.push({
          label: el.label,
          backgroundColor: BlueberryColorsMap[el.label],
          data: el.data || [],
        });
      });
    }

    // Sort by ripeness from flowers (if available), to green, to pink (yellow), to purple, to blue
    let sortedDatasets: ChartDataset<'bar', number[]>[] = [];
    if (datasets.length > 0) {
      sortedDatasets = orderBlueberriesByRipenessOrder({
        arrayToOrder: datasets,
        blueberryKeyGetter: (data) => data.label as string,
      });
    }

    const chartData: ChartDataWithRawData<'bar'> = {
      datasets: sortedDatasets,
      labels,
      rawData: rowsideEstimatedCounts.data,
    };

    return chartData;
  }, [rowsideEstimatedCounts.data, flowerFeatures]);

  return {
    ...rowsideEstimatedCounts,
    data: rowsideEstimatedCounts.data ? chartData : null,
  };
};
